<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import type { AccessibilityTextSize, NotificationPreferenceKey } from '../features/preferences';
import { notificationCueSources } from '../features/preferences';
import type { MediaDeviceOption } from '../jitsi/runtime';
import CButton from './primitives/CButton.vue';
import CIconButton from './primitives/CIconButton.vue';
import CSelect from './primitives/CSelect.vue';

type NotificationPreferenceDefinition = {
  key: NotificationPreferenceKey;
  label: string;
};

const textSizeOptions: Array<{
  description: string;
  label: string;
  testId: string;
  value: AccessibilityTextSize;
}> = [
  {
    description: 'Uses the current conference text size.',
    label: 'Default',
    testId: 'text-size-option-default',
    value: 'default',
  },
  {
    description: 'Makes menus, chat text, inputs, and buttons slightly larger.',
    label: 'Large',
    testId: 'text-size-option-large',
    value: 'large',
  },
  {
    description: 'Uses the largest accessibility text size for those controls.',
    label: 'Largest',
    testId: 'text-size-option-largest',
    value: 'largest',
  },
];

const props = withDefaults(
  defineProps<{
    audioInputOptions: MediaDeviceOption[];
    audioOutputOptions: MediaDeviceOption[];
    cameraOptions: MediaDeviceOption[];
    compact?: boolean;
    notificationPreferenceDefinitions: readonly NotificationPreferenceDefinition[];
    notificationPreferences: Record<NotificationPreferenceKey, boolean>;
    refreshDeviceOptions: () => Promise<void>;
    selectedAudioInput: string;
    selectedAudioOutput: string;
    selectedCamera: string;
    selectedCameraLabel: string;
    selectedTextSize: AccessibilityTextSize;
  }>(),
  {
    compact: false,
  },
);

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'update:selected-audio-input', value: string): void;
  (event: 'update:selected-audio-output', value: string): void;
  (event: 'update:selected-camera', value: string): void;
  (event: 'update:selected-text-size', value: AccessibilityTextSize): void;
  (event: 'update:notification-preference', value: { key: NotificationPreferenceKey; value: boolean }): void;
}>();

const activeTab = ref<'notifications' | 'media' | 'accessibility'>('notifications');
const speakerTestState = ref<'idle' | 'playing'>('idle');
const speakerTestError = ref<string | null>(null);
const microphoneTestActive = ref(false);
const microphoneTestError = ref<string | null>(null);
const microphoneLevel = ref(0);
const cameraPreviewActive = ref(false);
const cameraPreviewError = ref<string | null>(null);
const cameraPreviewRef = ref<HTMLVideoElement | null>(null);
const permissionState = ref<'idle' | 'requesting' | 'granted' | 'denied'>('idle');
const permissionError = ref<string | null>(null);

let speakerTestAudio: HTMLAudioElement | null = null;
let microphoneStream: MediaStream | null = null;
let cameraStream: MediaStream | null = null;
let microphoneAudioContext: AudioContext | null = null;
let microphoneAnimationFrame = 0;
let permissionRequestToken = 0;

const panelClass = computed(() =>
  props.compact
    ? 'rounded-xs border border-conference-border bg-conference-form p-2.5'
    : 'rounded-xs border border-conference-border bg-conference-form p-4',
);

function isPermissionDeniedError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError';
  }

  return error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError');
}

function isConstraintError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError';
  }

  return error instanceof Error && /constraints? could not be satisfied|overconstrained/i.test(error.message);
}

function describeMediaAccessError(kind: 'microphone' | 'camera', error: unknown): string {
  if (isPermissionDeniedError(error)) {
    return `Browser access to the ${kind} is blocked. Allow ${kind} permission for this site and try again.`;
  }

  if (isConstraintError(error)) {
    return `The selected ${kind} is unavailable. The browser can use the default ${kind} instead if one is connected.`;
  }

  return error instanceof Error ? error.message : `Unable to access the selected ${kind}.`;
}

async function requestPreferredStream(
  constraints: MediaStreamConstraints,
  fallbackConstraints: MediaStreamConstraints,
): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    if (!isConstraintError(error)) {
      throw error;
    }

    return navigator.mediaDevices.getUserMedia(fallbackConstraints);
  }
}

function stopMediaStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

function describePermissionRequestFailure(): string {
  return 'No permissions granted. Allow microphone and camera access to load your available media devices.';
}

async function requestDialogPermissions(): Promise<void> {
  permissionRequestToken += 1;
  const requestToken = permissionRequestToken;
  permissionState.value = 'requesting';
  permissionError.value = null;

  if (!navigator.mediaDevices?.getUserMedia) {
    permissionState.value = 'denied';
    permissionError.value = 'This browser cannot request microphone and camera access.';
    return;
  }

  try {
    const combinedStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    stopMediaStream(combinedStream);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      if (requestToken === permissionRequestToken) {
        permissionState.value = 'denied';
        permissionError.value = describePermissionRequestFailure();
      }
      return;
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stopMediaStream(audioStream);
    } catch (audioError) {
      if (isPermissionDeniedError(audioError)) {
        if (requestToken === permissionRequestToken) {
          permissionState.value = 'denied';
          permissionError.value = describePermissionRequestFailure();
        }
        return;
      }
    }

    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
      stopMediaStream(videoStream);
    } catch (videoError) {
      if (isPermissionDeniedError(videoError)) {
        if (requestToken === permissionRequestToken) {
          permissionState.value = 'denied';
          permissionError.value = describePermissionRequestFailure();
        }
        return;
      }
    }
  }

  if (requestToken !== permissionRequestToken) {
    return;
  }

  await props.refreshDeviceOptions();
  permissionState.value = 'granted';
  permissionError.value = null;
}

function updateSelectedAudioInput(event: Event): void {
  emit('update:selected-audio-input', (event.target as HTMLSelectElement).value);
}

function updateSelectedAudioOutput(event: Event): void {
  emit('update:selected-audio-output', (event.target as HTMLSelectElement).value);
}

function updateNotificationPreference(key: NotificationPreferenceKey, event: Event): void {
  emit('update:notification-preference', {
    key,
    value: (event.target as HTMLInputElement).checked,
  });
}

function updateSelectedTextSize(value: AccessibilityTextSize): void {
  emit('update:selected-text-size', value);
}

async function runSpeakerTest(): Promise<void> {
  speakerTestError.value = null;
  speakerTestState.value = 'playing';

  try {
    speakerTestAudio?.pause();
    speakerTestAudio = new Audio(notificationCueSources.joinedSessionDing);
    const sinkTarget = speakerTestAudio as HTMLAudioElement & { setSinkId?: (sinkId: string) => Promise<void> };

    if (sinkTarget.setSinkId && props.selectedAudioOutput && !props.selectedAudioOutput.startsWith('fallback-')) {
      await sinkTarget.setSinkId(props.selectedAudioOutput);
    }

    speakerTestAudio.currentTime = 0;
    await speakerTestAudio.play();
    await new Promise<void>((resolve) => {
      if (!speakerTestAudio) {
        resolve();
        return;
      }

      speakerTestAudio.onended = () => resolve();
      window.setTimeout(() => resolve(), 2_500);
    });
  } catch (error) {
    speakerTestError.value = error instanceof Error ? error.message : 'Unable to play the speaker test.';
  } finally {
    speakerTestState.value = 'idle';
  }
}

function stopMicrophoneTest(): void {
  if (microphoneAnimationFrame) {
    window.cancelAnimationFrame(microphoneAnimationFrame);
    microphoneAnimationFrame = 0;
  }

  microphoneStream?.getTracks().forEach((track) => track.stop());
  microphoneStream = null;

  if (microphoneAudioContext) {
    void microphoneAudioContext.close();
    microphoneAudioContext = null;
  }

  microphoneLevel.value = 0;
  microphoneTestActive.value = false;
}

async function startMicrophoneTest(): Promise<void> {
  microphoneTestError.value = null;
  stopMicrophoneTest();

  try {
    microphoneStream = await requestPreferredStream(
      {
        audio:
          props.selectedAudioInput && !props.selectedAudioInput.startsWith('fallback-')
            ? { deviceId: { exact: props.selectedAudioInput } }
            : true,
        video: false,
      },
      {
        audio: true,
        video: false,
      },
    );

    microphoneAudioContext = new AudioContext();
    const analyser = microphoneAudioContext.createAnalyser();
    analyser.fftSize = 256;
    const source = microphoneAudioContext.createMediaStreamSource(microphoneStream);
    const samples = new Uint8Array(analyser.frequencyBinCount);

    source.connect(analyser);
    microphoneTestActive.value = true;

    const measure = () => {
      analyser.getByteFrequencyData(samples);
      const peak = samples.reduce((currentPeak, sample) => Math.max(currentPeak, sample), 0);
      microphoneLevel.value = peak / 255;
      microphoneAnimationFrame = window.requestAnimationFrame(measure);
    };

    measure();
  } catch (error) {
    stopMicrophoneTest();
    microphoneTestError.value = describeMediaAccessError('microphone', error);
  }
}

function stopCameraPreview(): void {
  cameraStream?.getTracks().forEach((track) => track.stop());
  cameraStream = null;

  if (cameraPreviewRef.value) {
    cameraPreviewRef.value.srcObject = null;
  }

  cameraPreviewActive.value = false;
}

async function startCameraPreview(): Promise<void> {
  cameraPreviewError.value = null;
  stopCameraPreview();

  try {
    cameraStream = await requestPreferredStream(
      {
        audio: false,
        video:
          props.selectedCamera && !props.selectedCamera.startsWith('fallback-')
            ? { deviceId: { exact: props.selectedCamera } }
            : true,
      },
      {
        audio: false,
        video: true,
      },
    );

    if (cameraPreviewRef.value) {
      cameraPreviewRef.value.srcObject = cameraStream;
      await cameraPreviewRef.value.play();
    }

    cameraPreviewActive.value = true;
  } catch (error) {
    stopCameraPreview();
    cameraPreviewError.value = describeMediaAccessError('camera', error);
  }
}

onBeforeUnmount(() => {
  permissionRequestToken += 1;
  speakerTestAudio?.pause();
  stopMicrophoneTest();
  stopCameraPreview();
});

watch(
  activeTab,
  (tab) => {
    if (tab === 'media' && permissionState.value === 'idle') {
      void requestDialogPermissions();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div data-autofocus data-testid="media-preferences-panel" tabindex="-1" class="flex h-full min-h-0 flex-col bg-white">
    <div class="border-conference-line-soft flex items-center justify-between gap-3 border-b px-2.5 py-2">
      <div class="flex gap-2">
        <CButton
          size="sm"
          :tone="activeTab === 'notifications' ? 'accent' : 'subtle'"
          data-testid="preferences-notifications-tab"
          @click="activeTab = 'notifications'"
        >
          Bings and Dings
        </CButton>
        <CButton
          size="sm"
          :tone="activeTab === 'media' ? 'accent' : 'subtle'"
          data-testid="preferences-media-tab"
          @click="activeTab = 'media'"
        >
          Media Settings
        </CButton>
        <CButton
          size="sm"
          :tone="activeTab === 'accessibility' ? 'accent' : 'subtle'"
          data-testid="preferences-accessibility-tab"
          @click="activeTab = 'accessibility'"
        >
          Accessibility
        </CButton>
      </div>

      <CIconButton size="sm" tone="neutral" aria-label="Close preferences dialog" @click="emit('close')">
        <span aria-hidden="true">x</span>
      </CIconButton>
    </div>

    <div class="conference-text-control min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
      <div v-if="activeTab === 'notifications'" :class="[panelClass, 'space-y-3']">
        <p class="text-[11px] leading-5 text-slate-600">Choose which classroom sounds this browser should play.</p>
        <label
          v-for="preference in notificationPreferenceDefinitions"
          :key="preference.key"
          class="border-conference-line-soft flex items-center justify-between gap-3 rounded-[3px] border bg-white px-3 py-2.5"
        >
          <span class="text-[12px] font-semibold text-slate-950">{{ preference.label }}</span>
          <input
            :checked="notificationPreferences[preference.key]"
            class="accent-conference-accent-deep h-4 w-4"
            type="checkbox"
            @change="updateNotificationPreference(preference.key, $event)"
          />
        </label>
      </div>

      <div v-else-if="activeTab === 'media' && permissionState !== 'granted'" :class="[panelClass, 'space-y-3']">
        <p class="text-[12px] font-semibold text-slate-950">
          {{ permissionState === 'requesting' ? 'Requesting microphone and camera access...' : 'No permissions granted' }}
        </p>
        <p class="text-[11px] leading-5 text-slate-600">
          {{
            permissionState === 'requesting'
              ? 'The browser is being asked for microphone and camera access so the dialog can load your real media devices.'
              : (permissionError ?? 'Allow microphone and camera access to show available devices and media settings.')
          }}
        </p>
        <div class="rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-800">
          If the browser blocked the request, use the site permission controls in the address bar and try again.
        </div>
        <div class="flex justify-start">
          <CButton size="sm" tone="accent" :disabled="permissionState === 'requesting'" @click="requestDialogPermissions">
            {{ permissionState === 'requesting' ? 'Requesting...' : 'Request permissions again' }}
          </CButton>
        </div>
      </div>

      <div v-else-if="activeTab === 'media'" :class="[panelClass, 'space-y-3']">
        <p class="text-[11px] leading-5 text-slate-600">
          Choose your microphone and speakers, test them, and preview your camera.
        </p>

        <label class="border-conference-line-soft flex min-w-0 flex-col gap-1.5 rounded-[3px] border bg-white px-3 py-3">
          <span class="text-[11px] font-semibold tracking-[0.18em] text-slate-500 uppercase">Microphone</span>
          <CSelect :value="selectedAudioInput" data-testid="audio-input-select" @change="updateSelectedAudioInput">
            <option v-for="option in audioInputOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </CSelect>
        </label>

        <label class="border-conference-line-soft flex min-w-0 flex-col gap-1.5 rounded-[3px] border bg-white px-3 py-3">
          <span class="text-[11px] font-semibold tracking-[0.18em] text-slate-500 uppercase">Speaker</span>
          <CSelect :value="selectedAudioOutput" data-testid="audio-output-select" @change="updateSelectedAudioOutput">
            <option v-for="option in audioOutputOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </CSelect>
        </label>

        <div class="grid gap-3">
          <section class="border-conference-line-soft rounded-[3px] border bg-white px-3 py-3">
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[12px] font-semibold text-slate-950">Speaker test</p>
                <p class="mt-1 text-[11px] text-slate-600">Play the session join cue through the selected output.</p>
              </div>
              <CButton size="sm" tone="subtle" :disabled="speakerTestState === 'playing'" @click="runSpeakerTest">
                {{ speakerTestState === 'playing' ? 'Playing...' : 'Play test sound' }}
              </CButton>
            </div>
            <p v-if="speakerTestError" class="mt-2 text-[11px] text-rose-700">{{ speakerTestError }}</p>
          </section>

          <section class="border-conference-line-soft rounded-[3px] border bg-white px-3 py-3">
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[12px] font-semibold text-slate-950">Microphone test</p>
                <p class="mt-1 text-[11px] text-slate-600">Check live input level from the selected microphone.</p>
              </div>
              <CButton size="sm" tone="subtle" @click="microphoneTestActive ? stopMicrophoneTest() : startMicrophoneTest()">
                {{ microphoneTestActive ? 'Stop mic test' : 'Start mic test' }}
              </CButton>
            </div>
            <div class="mt-3 h-3 overflow-hidden rounded-full bg-slate-200">
              <div
                class="bg-conference-accent-deep h-full transition-[width] duration-100"
                :style="{ width: `${Math.max(6, microphoneLevel * 100)}%` }"
              />
            </div>
            <p class="mt-2 text-[11px] text-slate-600">
              {{ microphoneTestActive ? 'Speak to watch the level meter respond.' : 'Start the mic test to verify signal.' }}
            </p>
            <p v-if="microphoneTestError" class="mt-2 text-[11px] text-rose-700">{{ microphoneTestError }}</p>
          </section>

          <section class="border-conference-line-soft rounded-[3px] border bg-white px-3 py-3">
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-[12px] font-semibold text-slate-950">Camera preview</p>
                <p class="mt-1 text-[11px] text-slate-600">Preview the selected camera before going live.</p>
              </div>
              <CButton size="sm" tone="subtle" @click="cameraPreviewActive ? stopCameraPreview() : startCameraPreview()">
                {{ cameraPreviewActive ? 'Stop preview' : 'Start preview' }}
              </CButton>
            </div>
            <div class="border-conference-line-soft mt-3 aspect-video overflow-hidden rounded-[3px] border bg-slate-950">
              <video ref="cameraPreviewRef" class="h-full w-full object-cover" autoplay muted playsinline />
            </div>
            <p v-if="cameraPreviewError" class="mt-2 text-[11px] text-rose-700">{{ cameraPreviewError }}</p>
          </section>
        </div>
      </div>

      <div v-else :class="[panelClass, 'space-y-3']">
        <div class="space-y-1.5">
          <p class="text-[12px] font-semibold text-slate-950">Adjust conference text size</p>
          <p class="conference-text-helper text-slate-600">
            Menu entries, chat messages, text inputs, selects, and buttons use this setting. Headings stay at their current size.
          </p>
        </div>

        <div class="space-y-2" role="radiogroup" aria-label="Conference text size">
          <label
            v-for="option in textSizeOptions"
            :key="option.value"
            :data-testid="option.testId"
            class="border-conference-line-soft flex cursor-pointer items-start justify-between gap-3 rounded-[3px] border bg-white px-3 py-2.5"
          >
            <div class="space-y-1">
              <span class="conference-text-label block font-semibold text-slate-950">{{ option.label }}</span>
              <span class="conference-text-helper block text-slate-600">{{ option.description }}</span>
            </div>
            <input
              class="accent-conference-accent-deep mt-0.5 h-4 w-4"
              type="radio"
              name="conference-text-size"
              :value="option.value"
              :checked="selectedTextSize === option.value"
              @change="updateSelectedTextSize(option.value)"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
